import { useEffect } from 'react';
import { useAppContext } from '../context/AppContext';
import { updatePaymentStatus } from '../utils/supabase';

const TestingHelpers = () => {
  const {
    projects,
    setProjects,
    showNotification,
    currentUser
  } = useAppContext();

  // Hidden keyboard shortcuts for testing
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Only work if user is logged in and holds Ctrl/Cmd + Shift
      if (!currentUser || (!e.ctrlKey && !e.metaKey) || !e.shiftKey) return;

      const project = projects[projects.length - 1]; // Get most recent project
      if (!project) return;

      switch(e.key) {
        // Ctrl+Shift+U = Update Status
        case 'U':
        case 'u':
          updateProjectStatus(project);
          break;
        
        // Ctrl+Shift+D = Upload Documents
        case 'D':
        case 'd':
          uploadDocuments(project);
          break;
        
        // Ctrl+Shift+M = Send planAIdesign Message
        case 'M':
        case 'm':
          sendArchitectMessage(project);
          break;
        
        // Ctrl+Shift+Q = Send Quote (Stage 3/4)
        case 'Q':
        case 'q':
          if (project.stage >= 3) sendQuote(project);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentUser, projects]);

  const updateProjectStatus = (project) => {
    let newStatus, message;

    // Stage 1 flow
    if (project.stage === 1) {
      if (project.paymentStatus === 'partial') {
        // After initial payment, waiting for balance
        showNotification("Test Mode", "Balance payment required before documents can be uploaded", "info");
        return;
      } else if (project.paymentStatus === 'paid' && project.status === 'paid') {
        newStatus = 'feasibility-in-progress';
        message = "Payment confirmed. Working on your feasibility report...";
      } else if (project.status === 'feasibility-in-progress') {
        newStatus = 'ready-for-delivery';
        message = "Feasibility report is complete and ready for download!";
        
        // Also add the deliverable
        const deliverable = {
          type: "feasibility-report",
          title: "Site Investigation & Feasibility Report",
          description: "Complete feasibility assessment",
          files: ["Site_Investigation_Report.pdf", "Feasibility_Analysis.pdf"],
          uploadDate: new Date().toISOString().split('T')[0]
        };
        
        setProjects(prev => prev.map(p => {
          if (p.id === project.id) {
            return {
              ...p,
              status: newStatus,
              deliverables: [...(p.deliverables || []), deliverable],
              communications: [
                ...p.communications,
                {
                  type: "architect",
                  sender: "planAIdesign",
                  message: message,
                  timestamp: new Date().toISOString().slice(0, 16).replace('T', ' ')
                }
              ]
            };
          }
          return p;
        }));
        
        showNotification("Documents Ready", "Feasibility report has been uploaded!", "success");
        return;
      }
    }

    // Stage 2 flow
    else if (project.stage === 2) {
      if (project.paymentStatus === 'partial' && project.paymentsMade === 1) {
        if (project.paymentStructure.totalPayments === 2) {
          newStatus = 'awaiting-final-payment';
          message = "Initial concept work is progressing well. Final payment required to release documents.";
        } else {
          newStatus = 'awaiting-progress-payment';
          message = "Initial concept work is progressing well. Progress payment required to continue.";
        }
      } else if (project.paymentStatus === 'paid') {
        newStatus = 'concept-design-in-progress';
        message = "All payments received! Finalizing your concept design package...";
      } else if (project.status === 'concept-design-in-progress') {
        newStatus = 'initial-concept-ready';
        message = "Initial concept design is complete and ready for your review!";
      } else if (project.status.includes('revision') && project.status.includes('requested')) {
        const revNum = project.status.match(/revision-(\d+)/)[1];
        newStatus = `revision-${revNum}-in-progress`;
        message = `Working on revision ${revNum} based on your feedback...`;
      } else if (project.status.includes('revision') && project.status.includes('in-progress')) {
        newStatus = 'initial-concept-ready';
        message = "Revised concept is ready for your review!";
      } else {
        return;
      }
    }

    // Stage 3/4 flow
    else if (project.stage >= 3) {
      if (project.status === 'quote-in-progress') {
        newStatus = 'quote-ready';
        message = `Your ${project.stage === 3 ? 'authority approval' : 'construction'} documentation quote is ready!`;
      } else if (project.status === 'quote-approved' && project.paymentRequired) {
        showNotification("Test Mode", "Payment required before work can begin", "info");
        return;
      } else if (project.status === 'in-progress' && !project.documentsReady) {
        // Ready to upload documents
        message = "Working on your documentation...";
        showNotification("Test Mode", "Use Ctrl+Shift+D to simulate document upload", "info");
        return;
      }
    }

    if (newStatus) {
      setProjects(prev => prev.map(p => {
        if (p.id === project.id) {
          return {
            ...p,
            status: newStatus,
            communications: [
              ...p.communications,
              {
                type: "architect",
                sender: "planAIdesign",
                message: message,
                timestamp: new Date().toISOString().slice(0, 16).replace('T', ' ')
              }
            ]
          };
        }
        return p;
      }));
      
      // Also update Supabase for status changes
      updatePaymentStatus(project.id, project.paymentsMade || 0, newStatus, project.paymentStatus)
        .then(result => {
          if (result.success) {
            console.log('Supabase status update successful');
          }
        })
        .catch(err => console.error('Supabase status update failed:', err));
      
      showNotification("Status Updated", `Project status updated to: ${newStatus.replace(/-/g, ' ')}`, "success");
    }
  };

  const uploadDocuments = (project) => {
    if (project.stage === 1 && project.paymentStatus !== 'paid') {
      showNotification("Cannot Upload", "All payments must be completed first", "error");
      return;
    }

    if (project.stage === 2 && (!project.paymentsMade || project.paymentsMade < 1)) {
      showNotification("Cannot Upload", "Initial payment must be completed first", "error");
      return;
    }

    let deliverable;
    
    if (project.stage === 1) {
      deliverable = {
        type: "feasibility-report",
        title: "Site Investigation & Feasibility Report",
        description: "Complete feasibility assessment",
        files: ["Site_Investigation_Report.pdf", "Feasibility_Analysis.pdf", "Compliance_Summary.pdf"],
        uploadDate: new Date().toISOString().split('T')[0]
      };
    } else if (project.stage === 2) {
      const revRound = project.revisions?.currentRound || 0;
      deliverable = {
        type: "concept-design",
        title: `${revRound === 0 ? 'Initial' : `Revision ${revRound}`} Concept Design`,
        description: "Complete concept design package",
        files: ["Concept_Plans.pdf", "3D_Models.pdf", "Compliance_Tables.pdf"],
        uploadDate: new Date().toISOString().split('T')[0],
        version: revRound
      };
    } else if (project.stage >= 3) {
      if (project.status === 'quote-ready') {
        deliverable = {
          type: "quote-document",
          title: `Stage ${project.stage} Quote`,
          description: "Detailed quote and scope",
          files: [`Stage_${project.stage}_Quote.pdf`, "Scope_of_Work.pdf"],
          uploadDate: new Date().toISOString().split('T')[0]
        };
      } else {
        deliverable = {
          type: "project-documents",
          title: `Stage ${project.stage} Documentation`,
          description: `${project.stage === 3 ? 'Authority approval' : 'Construction'} documentation`,
          files: ["Documentation_Package.pdf", "Drawings.pdf", "Specifications.pdf"],
          uploadDate: new Date().toISOString().split('T')[0]
        };
      }
    }

    setProjects(prev => prev.map(p => {
      if (p.id === project.id) {
        return {
          ...p,
          deliverables: [...(p.deliverables || []), deliverable],
          documentsReady: true,
          status: project.stage === 2 ? 'initial-concept-ready' : p.status,
          communications: [
            ...p.communications,
            {
              type: "architect",
              sender: "planAIdesign",
              message: "Documentation has been uploaded for your review. Please download the files.",
              timestamp: new Date().toISOString().slice(0, 16).replace('T', ' ')
            }
          ]
        };
      }
      return p;
    }));
    
    showNotification("Documents Uploaded", "Test documents have been uploaded!", "success");
  };

  const sendArchitectMessage = (project) => {
    const messages = [
      "Thank you for your project submission. We're reviewing your requirements.",
      "Work is progressing well on your project. We'll have an update soon.",
      "We've made good progress today. The design is coming together nicely.",
      "Just a quick update - everything is on track with your project.",
      "Please let me know if you have any questions about the project so far."
    ];
    
    const message = messages[Math.floor(Math.random() * messages.length)];
    
    setProjects(prev => prev.map(p => {
      if (p.id === project.id) {
        return {
          ...p,
          communications: [
            ...p.communications,
            {
              type: "architect",
              sender: "planAIdesign",
              message: message,
              timestamp: new Date().toISOString().slice(0, 16).replace('T', ' ')
            }
          ]
        };
      }
      return p;
    }));
    
    showNotification("Message Sent", "planAIdesign message sent to client", "success");
  };

  const sendQuote = (project) => {
    if (project.status !== 'quote-in-progress') {
      showNotification("Not Ready", "Project must be in 'quote-in-progress' status", "warning");
      return;
    }

    const quoteAmount = project.stage === 3 ? "8,500" : "12,000";
    const initialPayment = project.stage === 3 ? "2,000" : "3,000";
    
    const deliverable = {
      type: "quote-document",
      title: `Stage ${project.stage} Quote`,
      description: "Detailed quote for services",
      files: [`Stage_${project.stage}_Quote.pdf`],
      uploadDate: new Date().toISOString().split('T')[0]
    };

    setProjects(prev => prev.map(p => {
      if (p.id === project.id) {
        return {
          ...p,
          status: 'quote-ready',
          deliverables: [...(p.deliverables || []), deliverable],
          quoteAmount: quoteAmount,
          initialPaymentAmount: initialPayment,
          communications: [
            ...p.communications,
            {
              type: "architect",
              sender: "planAIdesign",
              message: `Your quote is ready! Total: $${quoteAmount}. Initial payment of $${initialPayment} required upon approval.`,
              timestamp: new Date().toISOString().slice(0, 16).replace('T', ' ')
            }
          ]
        };
      }
      return p;
    }));
    
    showNotification("Quote Sent", "Quote has been sent to client", "success");
  };

  // Return null - this component doesn't render anything
  return null;
};

export default TestingHelpers;